
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  User,
  LogOut,
  Menu,
  X,
  Home,
  Settings,
  Bell,
  HelpCircle,
  Hotel,
  BarChart3,
} from "lucide-react";
import Image from "@/components/ui/image";

const TopNavbar = () => {
  const { user, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (path: string) => {
    return location.pathname === path ? "border-primary text-primary" : "border-transparent text-gray-500 hover:border-primary hover:text-primary";
  };

  return (
    <nav className="bg-white border-b border-gray-200 fixed w-full z-10">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard" className="flex items-center">
                <Image 
                  src="/lovable-uploads/84af94cd-12fe-494a-85ed-aed9f5638a4d.png" 
                  alt="PeakRevenue Logo" 
                  className="h-8 w-8 mr-2" 
                  objectFit="contain"
                />
                <span className="text-primary font-bold text-xl">PeakRev</span>
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link
                to="/dashboard"
                className={`${isActive("/dashboard")} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                <Home className="mr-2 h-4 w-4" />
                Dashboard
              </Link>
              <Link
                to="/reports"
                className={`${isActive("/reports")} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                Reports
              </Link>
              <Link
                to="/properties"
                className={`${isActive("/properties")} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                <Hotel className="mr-2 h-4 w-4" />
                Properties
              </Link>
            </div>
          </div>
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            {/* <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-red-600"></span>
            </Button> */}
            {/* <Button variant="ghost" size="icon">
              <HelpCircle className="h-5 w-5" />
            </Button> */}
            <div className="ml-3 relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center text-sm rounded-full focus:outline-none"
                  >
                    <User className="h-5 w-5 mr-2" />
                    <span className="hidden md:block">{user?.username}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator /> */}
                  <DropdownMenuItem onClick={signOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="-mr-2 flex items-center sm:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            <Link
              to="/dashboard"
              className={`${isActive("/dashboard") ? "bg-secondary text-primary border-l-4 border-primary" : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-primary"} block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Home className="inline-block mr-2 h-4 w-4" />
              Dashboard
            </Link>
            <Link
              to="/reports"
              className={`${isActive("/reports") ? "bg-secondary text-primary border-l-4 border-primary" : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-primary"} block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <BarChart3 className="inline-block mr-2 h-4 w-4" />
              Reports
            </Link>
            <Link
              to="/properties"
              className={`${isActive("/properties") ? "bg-secondary text-primary border-l-4 border-primary" : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-primary"} block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Hotel className="inline-block mr-2 h-4 w-4" />
              Properties
            </Link>
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-600" />
                </div>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800">
                  {user?.username}
                </div>
                <div className="text-sm font-medium text-gray-500">
                  {user?.attributes.email}
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <Button
                variant="ghost"
                className="w-full justify-start px-4 py-2 text-base font-medium text-gray-500 hover:text-primary hover:bg-gray-100"
                onClick={() => {
                  navigate("/settings");
                  setIsMobileMenuOpen(false);
                }}
              >
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start px-4 py-2 text-base font-medium text-gray-500 hover:text-primary hover:bg-gray-100"
                onClick={signOut}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default TopNavbar;
